import React, {FunctionComponent, useCallback, useEffect, useMemo, useState, useLayoutEffect} from "react";
import { ScaleExercise } from '../../models/scale-exercise';
import { useSaveExercise } from '../../services/graphql/use-exercises';
import Input from "../../components/forms/input";
import Button from "../../components/buttons/button";

interface AnnotationEditor {
  exercise: ScaleExercise;
  updateExercise: (newExercise: ScaleExercise) => any;
}

const AnnotationEditor: FunctionComponent<AnnotationEditor> = (props) => {
  const vm = useVM(props);
  
  return (
    <div className='flex items-end my-4'>
      <div className='flex-1'>
        <Input
          className='px-2'
          label='Left hand'
          onChange={(e) => vm.onChange('left_hand', e.target.value)}
          placeholder='Left hand'
          value={vm.leftHand || ''}
        />
      </div>

      <div className='flex-1'>
        <Input
          className='px-2'
          label='Right hand'
          onChange={(e) => vm.onChange('right_hand', e.target.value)}
          placeholder='Right hand'
          value={vm.rightHand || ''}
        />
      </div>

      <div className='flex-1 px-2'>
        <Button
          className='mx-2'
          kind='primary'
          onClick={() => vm.saveConfig()}
        >Save</Button>
        <span className={vm.savedCN}>Saved</span>
      </div>
    </div>
  );
}

type Config = Required<ScaleExercise>['config'];

function useVM({exercise, updateExercise}: AnnotationEditor) {
  const {saveExercise, data, loading, error} = useSaveExercise();
  const [prevLoadingState, setPrevLoadingState] = useState<'loading' | 'saved' | 'idle' | 'error'>(loading ? 'loading' : 'idle');

  const savedCN = useMemo(() => {
    return `duration-300 transition-opacity ${prevLoadingState === 'saved' ? 'opacity-1' : 'opacity-0'}`
  }, [prevLoadingState]);

  const saveConfig = useCallback(() => {
    const exerciseToSave = {
      ...exercise,
      config: {
        ...exercise.config,
      }
    };

    saveExercise({variables: {input: exerciseToSave}});
  }, [exercise]);

  const onChange = useCallback(function<T extends keyof Config> (field: T, value: string) {
    updateExercise({
      ...exercise,
      config: {
        ...(exercise.config || {}),
        [field]: value
      }
    });
  }, [exercise, updateExercise]);

  useEffect(() => {
    if (data) {
      updateExercise(data.savedExercise);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (error) {
      setPrevLoadingState('error');
    } else if (loading && prevLoadingState !== 'loading') {
      setPrevLoadingState('loading');
    } else if (!loading) {
      if (prevLoadingState === 'loading') {
        setPrevLoadingState('saved');
      } else {
        setTimeout(() => setPrevLoadingState('idle'), 3000);
      }
    } 
  }, [error, loading, prevLoadingState]);

  return {
    leftHand: (exercise.config || {}).left_hand,
    onChange,
    rightHand: (exercise.config || {}).right_hand,
    saveConfig,
    savedCN,
  }
}

export default AnnotationEditor;
